import { ChevronDown, MessageCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { PageHeader } from '../components/PageHeader';

interface FAQItem {
  id: string
  question: string
  answer: string
}

const faqItems: FAQItem[] = [
  {
    id: '1',
    question: 'How do I add a new customer?',
    answer: 'Navigate to the Customers page and click the "Add Customer" button. Fill in the required information (name, phone number, etc.) and submit. The customer will be created and you can then add vehicles and documents for that customer.',
  },
  {
    id: '2',
    question: 'How do I register a vehicle?',
    answer: 'Go to the customer details page and navigate to their vehicles. Click "Add Vehicle" and provide the registration number, make, model, and other vehicle details. Once registered, you can add documents like licenses, insurances, PUCs, fitness certificates, road taxes, and environmental taxes.',
  },
  {
    id: '3',
    question: 'What documents can I manage?',
    answer: 'You can manage the following documents: Licences, Insurances, PUCs (Pollution Under Control), Fitness Certificates, Road Taxes, and Environmental Taxes. Each document type tracks expiry dates and allows you to filter by status (Active, Expiring Soon, Expired).',
  },
  {
    id: '4',
    question: 'How do I track expiring documents?',
    answer: 'All document pages have status filters: Active (30+ days remaining), Expiring Soon (within 30 days), and Expired (past expiry date). The system automatically calculates status based on expiry dates for real-time tracking.',
  },
  {
    id: '5',
    question: 'Can I edit document details after creation?',
    answer: 'Yes, all documents can be edited. Navigate to the document list, click the edit button (pencil icon) on the document you want to modify, make your changes, and save. The table will automatically refresh with updated information.',
  },
  {
    id: '6',
    question: 'How do I manage payments?',
    answer: 'The Payments page shows all payment transactions. You can track payment status, payment modes (online, cash, DD, cheque, NEFT), and payment dates for all your documents.',
  },
  {
    id: '7',
    question: 'What does the Dashboard show?',
    answer: 'The Dashboard provides an overview of your agency activities including key metrics, recent transactions, document status summaries, and quick access to important information.',
  },
  {
    id: '8',
    question: 'How do I update my agency settings?',
    answer: 'Go to the Settings page to update your agency information, preferences, and account details. Changes are saved automatically when you update the form.',
  },
]

export function HelpPage() {
  const navigate = useNavigate()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const toggleExpanded = (id: string) => {
    setExpandedId(expandedId === id ? null : id)
  }

  return (
    <>
      <PageHeader title="Help & Support" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
        {/* Quick Links */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h3>
          <div className="space-y-3">
            <a href="#faq" className="block text-saffron hover:text-orange-600 font-medium">
              → Frequently Asked Questions
            </a>
            <a href="#features" className="block text-saffron hover:text-orange-600 font-medium">
              → Key Features
            </a>
            <a href="#contact" className="block text-saffron hover:text-orange-600 font-medium">
              → Contact Us
            </a>
          </div>
        </div>

        {/* Documentation */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Getting Started</h3>
          <div className="space-y-2 text-gray-700 text-sm">
            <p>• Create an account and set up your agency</p>
            <p>• Add customers to your management system</p>
            <p>• Register vehicles for each customer</p>
            <p>• Track documents and their expiry dates</p>
            <p>• Monitor payments and transactions</p>
          </div>
        </div>

        {/* Contact Summary */}
        <div className="bg-saffron/10 rounded-lg border border-saffron p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
          <p className="text-gray-700 text-sm mb-4">
            Can't find what you're looking for? Contact our support team via email or WhatsApp.
          </p>
          <Button onClick={() => navigate('/contact-us')} className="w-full bg-saffron">
            Contact Support
          </Button>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="bg-white rounded-lg border border-gray-200 p-8 mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Customer Management</h4>
            <p className="text-gray-700 text-sm">
              Organize and manage all your customers in one centralized location with detailed profiles.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Vehicle Tracking</h4>
            <p className="text-gray-700 text-sm">
              Register and track multiple vehicles per customer with complete vehicle details.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Document Management</h4>
            <p className="text-gray-700 text-sm">
              Manage licenses, insurances, PUCs, fitness certificates, taxes, and more in one place.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Expiry Tracking</h4>
            <p className="text-gray-700 text-sm">
              Automatic status tracking with Active, Expiring Soon, and Expired categories.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Payment Monitoring</h4>
            <p className="text-gray-700 text-sm">
              Track all payments with multiple modes: online, cash, DD, cheque, and NEFT.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Real-time Updates</h4>
            <p className="text-gray-700 text-sm">
              All changes are reflected instantly across the platform with automatic table refresh.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-8 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </div>

        <div className="divide-y divide-gray-200">
          {faqItems.map((item) => (
            <div key={item.id} className="border-b border-gray-100 last:border-b-0">
              <button
                onClick={() => toggleExpanded(item.id)}
                className="w-full px-8 py-4 text-left hover:bg-gray-50 transition-colors flex items-center justify-between group"
              >
                <span className="font-medium text-gray-900 group-hover:text-saffron">
                  {item.question}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    expandedId === item.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {expandedId === item.id && (
                <div className="px-8 py-4 bg-gray-50 text-gray-700 text-sm leading-relaxed">
                  {item.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact" className="mt-12 bg-gradient-to-r from-saffron/10 to-orange-50 rounded-lg border border-saffron p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Still need help?</h2>
        <p className="text-gray-700 mb-6">
          Our support team is ready to assist you. Reach out via email or WhatsApp.
        </p>
        <div className="flex flex-col sm:flex-row gap-6 justify-center">
          <div>
            <p className="text-sm text-gray-600 mb-2">Email</p>
            <a href="mailto:connect@iqueuelab.com" className="text-lg font-semibold text-saffron hover:text-orange-600">
              connect@iqueuelab.com
            </a>
          </div>
          <Button 
            onClick={() => window.open('https://wa.me/917620769511', '_blank')}
            variant="secondary" 
            className="border-saffron text-saffron hover:bg-saffron/10"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            WhatsApp
          </Button>
        </div>
      </div>
    </>
  )
}
